import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { mfaActionFromPostgrestBody, type MfaErrorAction } from './mfaGate';

// Port of lib/core/supabase_service.dart.
//
// In Flutter the URL + anon key arrived via --dart-define at build time;
// the web equivalent is Vite's `import.meta.env.VITE_*`, inlined at build
// time and validated here so a missing config fails loudly (and is caught
// by main.tsx, which renders the ConfigError screen instead of crashing).

const url = import.meta.env.VITE_SUPABASE_URL ?? '';
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

export class SupabaseConfigError extends Error {
  constructor(detail: string) {
    super(
      `${detail} Vite inlines VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY from ` +
        'web/.env AT BUILD TIME — edit web/.env (NOT .env.example) with real ' +
        'values and rebuild. (Switching stacks? The anon key must match the URL.)',
    );
    this.name = 'SupabaseConfigError';
  }
}

// A value still carrying an .env.example placeholder ("your-anon-key-here",
// "sb_…_PLACEHOLDER") is the failure we keep hitting: it's non-empty, so a bare
// `!value` check passes and the app silently boots against a stack it can't
// authenticate to (every request 401s → blank screen). Treat placeholders as
// "not configured" so the ConfigError screen shows loudly instead.
function isPlaceholder(v: string): boolean {
  const s = v.trim().toLowerCase();
  return (
    s === '' ||
    s.includes('placeholder') ||
    s.includes('your-anon-key') ||
    s.includes('your-supabase-url') ||
    s.endsWith('-here')
  );
}

export function assertSupabaseConfigured(): void {
  if (isPlaceholder(url)) throw new SupabaseConfigError('VITE_SUPABASE_URL is missing or a placeholder.');
  if (isPlaceholder(anonKey)) throw new SupabaseConfigError('VITE_SUPABASE_ANON_KEY is missing or a placeholder.');
}

// --- Two-factor signals (port of lib/core/mfa_http_client.dart) -----------
// A `42501 mfa_required` from ANY PostgREST request means this tab's session
// is aal1 for a user who enrolled (perhaps on another device); `42501
// admin_mfa_required` means an admin tool needs a session with the code
// (mfa-contract §3.4, §4b). One fetch wrapper sees every response, so no
// repository has to catch these itself. authBootstrap.ts acts on them.

type MfaSignal = Extract<MfaErrorAction, 'refreshAndGate' | 'adminGate'>;
const mfaListeners = new Set<(action: MfaSignal) => void>();

/** Subscribes to two-factor signals; returns the unsubscriber. */
export function onMfaSignal(listener: (action: MfaSignal) => void): () => void {
  mfaListeners.add(listener);
  return () => { mfaListeners.delete(listener); };
}

/** Wraps fetch: only error responses (401/403) under /rest/v1/ are read —
 *  from a clone(), so the caller still gets the untouched original and its
 *  usual PostgrestError. Everything else streams through. */
export function mfaSignalFetch(
  inner: typeof fetch = (...a) => fetch(...a),
  emit: (action: MfaSignal) => void = (a) => mfaListeners.forEach((l) => l(a)),
): typeof fetch {
  return async (input, init) => {
    const res = await inner(input, init);
    if (res.status !== 401 && res.status !== 403) return res;
    const href = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (!href.includes('/rest/v1/')) return res;
    try {
      const action = mfaActionFromPostgrestBody(await res.clone().text());
      if (action === 'refreshAndGate' || action === 'adminGate') emit(action);
    } catch {
      // Unreadable body: not a two-factor signal.
    }
    return res;
  };
}

// Single shared client for the whole app — the analog of Supabase.instance
// .client in the Flutter SDK. PKCE flow so the email-confirmation / magic
// -link `?code=` exchange in authBootstrap.ts works (see that file).
export const supabase: SupabaseClient = createClient(url, anonKey, {
  auth: {
    flowType: 'pkce',
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true, // handles `#access_token=…` implicit-flow hash
  },
  // Sees every PostgREST error once, for the two-factor gate.
  global: { fetch: mfaSignalFetch() },
});
